import React from "react";
import { Avatar, Box, Button, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { X, Mail, Phone, MapPin, Briefcase, Clock, Download, ExternalLink } from "lucide-react";

const formatRelativeTime = (dateString, locale) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  const units = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];

  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds || unit === "minute") {
      return rtf.format(Math.round(diffSeconds / seconds), unit);
    }
  }

  return "";
};

const formatDayLabel = (dateString, locale) => {
  if (!dateString) return "";

  return new Date(dateString).toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const BRAND = "hsl(153, 68%, 25%)";
const INK = "hsl(178, 93%, 10%)";
const CARD_BG = "hsl(160, 10%, 97%)";
const CARD_BORDER = "1px solid hsl(160, 10%, 90%)";
const FONT_SEMI = "Segoe UI Semibold, system-ui, sans-serif";
const FONT_BOLD = "Segoe UI Bold, system-ui, sans-serif";

const DENIAL_REASON_OPTIONS = ["wrong_diploma", "wrong_xp", "insufficient_languages", "closed_position", "other"];

const STATUS_STYLES = {
  pending: { bgcolor: "rgba(228, 161, 27, 0.12)", color: "#B7820A" },
  open: { bgcolor: "hsla(153, 68%, 25%, 0.1)", color: BRAND },
  accepted: { bgcolor: "hsla(153, 68%, 25%, 0.15)", color: BRAND },
  saved: { bgcolor: "rgba(0, 0, 0, 0.06)", color: "rgba(0, 0, 0, 0.6)" },
  closed: { bgcolor: "rgba(0, 0, 0, 0.06)", color: "rgba(0, 0, 0, 0.5)" },
};

const Chip = ({ children, sx = {} }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.25,
      py: 0.25,
      fontSize: "11px",
      fontFamily: FONT_SEMI,
      bgcolor: "hsla(153, 68%, 25%, 0.1)",
      color: BRAND,
      borderRadius: "9999px",
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionTitle = ({ id }) => (
  <Typography
    variant="body3bold"
    sx={{
      display: "block",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "hsla(178, 20%, 35%, 0.7)",
      fontFamily: FONT_BOLD,
      mb: 1,
    }}
  >
    <FormattedMessage id={id} />
  </Typography>
);

const InfoRow = ({ icon: Icon, children, href }) => (
  <Box
    component={href ? "a" : "div"}
    href={href}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      py: 0.4,
      textDecoration: "none",
      color: INK,
      ...(href ? { "&:hover": { color: BRAND, "& span": { textDecoration: "underline" } } } : {}),
    }}
  >
    <Icon size={14} color={BRAND} style={{ flexShrink: 0 }} />
    <Typography component="span" variant="body2" sx={{ color: "inherit", minWidth: 0, wordBreak: "break-word" }}>
      {children}
    </Typography>
  </Box>
);

const CandidateContextSidebar = ({ detail, onClose, inline = false }) => {
  const theme = useTheme();
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const candidate = detail?.candidate;
  if (!candidate) return null;

  const kanban = detail?.kanban;
  const applications = detail?.job_applications || [];
  const portfolioUrl = candidate.portfolio_url || candidate.portfolio_file_url;

  const formatReasons = (reasons) =>
    reasons
      .map((reason) =>
        DENIAL_REASON_OPTIONS.includes(reason)
          ? intl.formatMessage({ id: `applicant.denial_reasons.${reason}`, defaultMessage: reason })
          : reason
      )
      .join(", ");

  return (
    <Box
      sx={{
        width: inline ? "100%" : { md: "350px" },
        flexShrink: 0,
        bgcolor: theme.palette.common.white,
        border: inline ? "none" : "1px solid hsla(160, 10%, 90%, 0.6)",
        borderBottom: inline ? "1px solid hsl(160, 10%, 92%)" : undefined,
        borderRadius: inline ? 0 : "16px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, pt: 1 }}>
        <IconButton
          size="small"
          onClick={onClose}
          title={intl.formatMessage({ id: "recruiter.messaging.close_panel" })}
          sx={{ color: "hsla(178, 20%, 35%, 0.7)" }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: 2, pb: 2 }}>
        <Avatar
          src={candidate.profile_picture_url || undefined}
          alt={candidate.full_name}
          sx={{ width: 72, height: 72, mb: 1 }}
        />
        <Typography variant="h6" sx={{ color: INK, textAlign: "center", fontFamily: FONT_BOLD }}>
          {candidate.full_name}
        </Typography>
        {candidate.job_title && (
          <Typography variant="body3" sx={{ color: "hsla(178, 20%, 35%, 0.8)", textAlign: "center" }}>
            {candidate.job_title}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.75, mt: 1 }}>
          {candidate.region && (
            <Chip>
              <MapPin size={11} />{" "}
              {intl.formatMessage({ id: `regions.${candidate.region}`, defaultMessage: candidate.region })}
            </Chip>
          )}
          {candidate.experience_year && (
            <Chip>
              <Briefcase size={11} />{" "}
              {intl.formatMessage(
                { id: "recruiter.candidate_experience_pastille" },
                { count: candidate.experience_year }
              )}
            </Chip>
          )}
        </Box>
      </Box>

      {/* Contact card */}
      {(candidate.email || candidate.phone || candidate.last_active_at) && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ bgcolor: CARD_BG, border: CARD_BORDER, borderRadius: "12px", p: 1.5 }}>
            {candidate.email && (
              <InfoRow icon={Mail} href={`mailto:${candidate.email}`}>
                {candidate.email}
              </InfoRow>
            )}
            {candidate.phone && (
              <InfoRow icon={Phone} href={`tel:${candidate.phone}`}>
                {candidate.phone}
              </InfoRow>
            )}
            {candidate.last_active_at && (
              <InfoRow icon={Clock}>
                <FormattedMessage id="recruiter.last_login" />: {formatRelativeTime(candidate.last_active_at, i18nLocale)}
              </InfoRow>
            )}

            {(candidate.resume_url || portfolioUrl) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                {candidate.resume_url && (
                  <Button
                    component="a"
                    href={candidate.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="rounded"
                    color="primaryContained"
                    size="small"
                    startIcon={<Download size={14} />}
                    sx={{ flex: 1, minWidth: "fit-content", textTransform: "none", fontFamily: FONT_SEMI, fontSize: "12px" }}
                  >
                    <FormattedMessage id="recruiter.kanban.download_cv" />
                  </Button>
                )}
                {portfolioUrl && (
                  <Button
                    component="a"
                    href={portfolioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="rounded"
                    color="primaryOutlined"
                    size="small"
                    startIcon={<ExternalLink size={14} />}
                    sx={{ flex: 1, minWidth: "fit-content", textTransform: "none", fontFamily: FONT_SEMI, fontSize: "12px" }}
                  >
                    <FormattedMessage id="recruiter.kanban.view_portfolio" />
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Kanban */}
      {kanban?.column_name && (
        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid hsl(160, 10%, 94%)" }}>
          <SectionTitle id="recruiter.messaging.kanban" />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip>{kanban.column_name}</Chip>
            {kanban.in_column_since && (
              <Typography variant="body3" sx={{ color: "hsla(178, 20%, 35%, 0.7)" }}>
                <FormattedMessage id="recruiter.messaging.in_stage_since" /> {formatDayLabel(kanban.in_column_since, i18nLocale)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Job applications */}
      {applications.length > 0 && (
        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid hsl(160, 10%, 94%)" }}>
          <SectionTitle id="recruiter.kanban.job_applications" />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {applications.map((application) => {
              const reasons =
                application.status === "closed" && application.denial_reasons?.length > 0
                  ? formatReasons(application.denial_reasons)
                  : null;

              return (
                <Box
                  key={application.id}
                  sx={{ bgcolor: CARD_BG, border: CARD_BORDER, borderRadius: "12px", p: 1.25 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="body2bold" noWrap sx={{ color: INK, minWidth: 0, fontFamily: FONT_SEMI }}>
                      {application.job_title || "—"}
                    </Typography>
                    {application.status === "closed" && (
                      <Chip sx={STATUS_STYLES.closed}>
                        {application.status}
                      </Chip>
                    )}
                  </Box>
                  <Typography variant="body3" sx={{ color: "hsla(178, 20%, 35%, 0.7)" }}>
                    <FormattedMessage id="recruiter.messaging.since" /> {formatDayLabel(application.created_at, i18nLocale)}
                  </Typography>
                  {reasons && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="body3"
                        sx={{ display: "block", color: theme.palette.error.main, fontFamily: FONT_SEMI }}
                      >
                        <FormattedMessage id="applicant.denial_reason" /> {reasons}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CandidateContextSidebar;
